import { compileFetchedEntity } from '../shared/compile-fetched-entity';
import { itemsCol, layerEntriesOf, layerHas, layerListEntryUuid, layerOf } from '../shared/array-types';
import { fromBaseRecord } from '../janus-engine/compile/from-base-context';
import { statusRuneMountHtml } from '../shared/status-rune';
import { buildNamesMapFromBlob } from './build-names-map';

// Spell + AbilityType family, including trap abilities; selectors and objects keep their type icon.
const RUNE_TYPES = new Set(['spell', 'passive_ability', 'full_ability', 'ability', 'quick_ability',
    'attack_ability', 'free_ability', 'reaction_ability', 'opportunity_attack_ability', 'curse',
    'menta', 'sanguine', 'lair_ability', 'trap_ability']);
const jobs = new WeakMap<HTMLElement, object>();

/** Read the compiled definition through the same readers as the native requirements block. */
export function sfxReviewRequirements(row: Record<string, unknown>): string {
    let ids: string[] = [];
    for (const layer of ['requires_subclass', 'requires_class']) {
        ids = layerEntriesOf(row, layer).map(entry => layerListEntryUuid(entry)
            || (typeof entry === 'string' ? entry : '')).filter(Boolean);
        if (ids.length) break;
    }
    const names = buildNamesMapFromBlob(row);
    const level = layerHas(row, 'requires_level') ? layerOf(row, 'requires_level') : null;
    const classLevel = layerHas(row, 'requires_class_level') ? layerOf(row, 'requires_class_level') : null;
    const pill = (value: unknown, title: string) => `<span class="lvl" title="${title}">${escapeHtml(String(value))}</span>`;
    return (level != null ? pill(level, 'requires level') : '') + ids.map(id => {
        const name = names[id.toUpperCase()]?.item_name || id;
        return `<a class="cls" href="/item.php?id=${encodeURIComponent(id)}" target="_blank" rel="noopener" title="${escapeHtml(name)}" aria-label="${escapeHtml(name)}">${iconMap(id, 32, 32)}${classLevel != null ? `<span class="clv">${escapeHtml(String(classLevel))}</span>` : ''}</a>`;
    }).join('') + (!ids.length && classLevel != null ? pill(classLevel, 'requires class level') : '');
}

/** Hydrate only the two header islands: review, playback and notes retain their own DOM. */
export async function hydrateSfxReviewHeader(header: HTMLElement): Promise<void> {
    const id = header.dataset.ftSfxDefinition ?? '', type = header.dataset.ftSfxType ?? '';
    const label = header.dataset.ftSfxLabel ?? id, pool = header.closest<HTMLElement>('[data-effect]');
    const effect = pool?.dataset.effect;
    const requirements = header.querySelector<HTMLElement>('[data-ft-sfx-requirements]');
    const art = header.querySelector<HTMLElement>('[data-ft-sfx-art]');
    const diagnostic = header.querySelector<HTMLElement>('[data-ft-sfx-diagnostic]');
    if (!requirements || !art || !diagnostic) return;
    const job = {};
    jobs.set(header, job);
    const owns = () => jobs.get(header) === job && header.isConnected;
    const current = () => owns() && header.dataset.ftSfxDefinition === id && header.dataset.ftSfxType === type
        && header.closest('[data-effect]') === pool && pool?.dataset.effect === effect;
    header.dataset.ftSfxHeader = 'pending';
    art.innerHTML = iconMap(type, 28, 28);
    requirements.replaceChildren(); diagnostic.textContent = '';
    try {
        const response = await fetch(`/options/api/authored_card_inputs_inc.php?id=${encodeURIComponent(id)}`);
        if (!response.ok) throw new Error(`sfx-header:definition-http-${response.status}`);
        const raw: unknown = await response.json();
        const envelope = fromBaseRecord(raw) && fromBaseRecord(raw._entity_base) ? raw._entity_base : null;
        // The endpoint can fall back for unsupported identities. Only its raw definition lane belongs here.
        if (!envelope || envelope.entity !== 'definition' || envelope.entity_id !== id
            || itemsCol(raw, 'item_id') !== id || itemsCol(envelope.base, 'item_id') !== id)
            throw new Error('sfx-header:definition-identity');
        if (!current()) throw new Error('sfx-header:stale-definition');
        const row = await compileFetchedEntity(raw);
        if (!current()) throw new Error('sfx-header:stale-definition');
        if (typeof row._entity_refusal === 'string') throw new Error(row._entity_refusal);
        if (itemsCol(row, 'item_id') !== id || itemsCol(row, 'item_type') !== type
            || !fromBaseRecord(row.item_layer_types)) throw new Error('sfx-header:compile-incomplete');
        const html = sfxReviewRequirements(row);
        if (RUNE_TYPES.has(type)) {
            if (!window.ftMountRuneIcons) throw new Error('sfx-header:rune-renderer-unavailable');
            art.innerHTML = statusRuneMountHtml(row, { size: 44, className: 'ft-rune-inline' });
            window.ftMountRuneIcons(art);
        }
        requirements.innerHTML = html;
        window.ftMountIcons?.(requirements);
        header.dataset.ftSfxHeader = 'ready';
    } catch (error) {
        if (!owns()) return;
        header.dataset.ftSfxHeader = 'failed';
        requirements.replaceChildren();
        diagnostic.textContent = `${label}: header unavailable (${error instanceof Error ? error.message : String(error)})`;
    }
}

function mountSfxReviewHeaders(): void {
    document.querySelectorAll<HTMLElement>('#sfrev [data-ft-sfx-definition]').forEach(header => {
        void hydrateSfxReviewHeader(header);
    });
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', mountSfxReviewHeaders, { once: true });
else mountSfxReviewHeaders();
